import React from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';

import { Modal } from 'shared/components';

import CreateAppointmentDiscardModal from 'modules/appointmentsManagement/components/_modals/CreateAppointmentDiscardModal';
import UpdateAppointmentDiscardModal from 'modules/appointmentsManagement/components/_modals/UpdateAppointmentDiscardModal';
import FetchedByAnotherProviderModal from 'modules/appointmentsManagement/components/_modals/FetchedByAnotherProviderModal';
import ConfirmUpdateGroupedAppointmentModal from 'modules/appointmentsManagement/components/_modals/ConfirmUpdateGroupedAppointmentModal';
import CannotCreateOrUpdateAppointmentErrorModal from 'modules/appointmentsManagement/components/_modals/CannotCreateOrUpdateAppointmentErrorModal';
import EditUserModal from 'modules/user/components/_modals/EditUserModal';
import ConfirmEditUserCancelModal from 'modules/user/components/_modals/ConfirmEditUserCancelModal';
import DefaultModal from '../DefaultModal';
import SignOutModal from '../_modals/SignOutModal';
import ModalSignOutDuringConsultation from '../_modals/ModalSignOutDuringConsultation';
import ModalStartConsultation from '../_modals/ModalStartConsultation';
import ModalEndConsultation from '../_modals/ModalEndConsultation';
import ModalCancelAppointment from '../_modals/ModalCancelAppointment';
import ModalUnableToCancel from '../_modals/ModalUnableToCancel/ModalUnableToCancel';
import ModalNotificationAboutStartConsultation from '../_modals/ModalNotificationAboutStartConsultation';
import ModalNotifyAboutStartingOrMissedConsultation from '../_modals/ModalNotifyAboutStartingOrMissedConsultation';
import ModalRetryUploadMeta from '../_modals/ModalRetryUploadMeta';
import ModalConsultationMediaDelete from '../_modals/ModalConsultationMediaDelete';
import ModalUpdateUploadFile from '../_modals/ModalUpdateUploadFile';
import ModalEditFileName from '../_modals/ModalEditFileName';
import ModalUploadExtensionFailed from '../_modals/ModalUploadExtensionFailed';
import ModalUploadSizeFailed from '../_modals/ModalUploadSizeFailed';
import ModalUploadMetaFailed from '../_modals/ModalUploadMetaFailed';
import ModalSignOutToProceed from '../_modals/ModalSignOutToProceed';
import TermsAndConditionsLoginModal from '../_modals/TermsAndConditionsLoginModal';
import TermsAndConditionsModal from '../_modals/TermsAndConditionsModal';
import TermsAndConditionsDeclineModal from '../_modals/TermsAndConditionsDeclineModal';
import ModalDiscardCreatingAppointment from '../_modals/ModalDiscardCreatingAppointment';
import ModalOOHAppointment from '../_modals/ModalOOHAppointment';
import ModalCannotCreateAppointment from '../_modals/ModalCannotCreateAppointment';
import AvatarUploadIncorrectExtensionModal from '../_modals/AvatarUploadIncorrectExtensionModal';
import AvatarUploadFileSizeExceededModal from '../_modals/AvatarUploadFileSizeExceededModal';
import AvatarUploadFailureModal from '../_modals/AvatarUploadFailureModal';
import ModalAccessibilityStatement from '../_modals/ModalAccessibilityStatement';
import ModalAppointmentForwardingConsent from '../_modals/ModalAppointmentForwardingConsent';
import * as modalCodes from '../../constants/modalCodes';

const modalMap = {
  [modalCodes.SIGN_OUT_MODAL]: SignOutModal,
  [modalCodes.MODAL_SIGN_OUT_DURING_CONSULTATION]: ModalSignOutDuringConsultation,
  [modalCodes.MODAL_TERMS_AND_CONDITIONS_LOGIN]: TermsAndConditionsLoginModal,
  [modalCodes.MODAL_TERMS_AND_CONDITIONS]: TermsAndConditionsModal,
  [modalCodes.MODAL_TERMS_AND_CONDITIONS_APPROVE_DECLINE_MODAL]: TermsAndConditionsDeclineModal,
  [modalCodes.MODAL_START_CONSULTATION]: ModalStartConsultation,
  [modalCodes.MODAL_END_CONSULTATION]: ModalEndConsultation,
  [modalCodes.MODAL_CANCEL_APPOINTMENT]: ModalCancelAppointment,
  [modalCodes.MODAL_UNABLE_TO_CANCEL_APPOINTMENT]: ModalUnableToCancel,
  [modalCodes.MODAL_NOTIFICATION_ABOUT_START_CONSULTATION]: ModalNotificationAboutStartConsultation,
  [modalCodes.MODAL_NOTIFICATION_ABOUT_MISSED_CONSULTATION]: ModalNotifyAboutStartingOrMissedConsultation,
  [modalCodes.MODAL_RETRY_UPLOAD_META]: ModalRetryUploadMeta,
  [modalCodes.MODAL_CONSULTATION_MEDIA_DELETE]: ModalConsultationMediaDelete,
  [modalCodes.MODAL_UPDATE_UPLOAD_FILE]: ModalUpdateUploadFile,
  [modalCodes.MODAL_EDIT_FILE_NAME]: ModalEditFileName,
  [modalCodes.MODAL_UPLOAD_SIZE_FAILED]: ModalUploadSizeFailed,
  [modalCodes.MODAL_UPLOAD_EXTENSION_FAILED]: ModalUploadExtensionFailed,
  [modalCodes.MODAL_UPLOAD_META_FAILED]: ModalUploadMetaFailed,
  [modalCodes.MODAL_SIGN_OUT_TO_PROCEED]: ModalSignOutToProceed,
  [modalCodes.MODAL_DISCARD_CREATING_APPOINTMENT]: ModalDiscardCreatingAppointment,
  [modalCodes.MODAL_OOH_APPOINTMENT]: ModalOOHAppointment,
  [modalCodes.MODAL_CANNOT_CREATE_APPOINTMENT]: ModalCannotCreateAppointment,
  [modalCodes.UPLOAD_AVATAR_INCORRECT_EXTENSION_MODAL]: AvatarUploadIncorrectExtensionModal,
  [modalCodes.UPLOAD_AVATAR_SIZE_EXCEEDED]: AvatarUploadFileSizeExceededModal,
  [modalCodes.UPLOAD_AVATAR_FAILURE_MODAL]: AvatarUploadFailureModal,
  [modalCodes.MODAL_ACCESSIBILITY_STATEMENT]: ModalAccessibilityStatement,
  [modalCodes.EDIT_USER_MODAL]: EditUserModal,
  [modalCodes.CONFIRM_CANCEL_USER_EDIT_MODAL]: ConfirmEditUserCancelModal,
  [modalCodes.APPOINTMENT_FORWARDING_CONSENT_MODAL]: ModalAppointmentForwardingConsent,
  [modalCodes.CREATE_APPOINTMENT_DISCARD_MODAL]: CreateAppointmentDiscardModal,
  [modalCodes.UPDATE_APPOINTMENT_DISCARD_MODAL]: UpdateAppointmentDiscardModal,
  [modalCodes.CANNOT_CREATE_OR_UPDATE_APPOINTMENT_MODAL]: CannotCreateOrUpdateAppointmentErrorModal,
  [modalCodes.FETCHED_BY_ANOTHER_PROVIDER_MODAL]: FetchedByAnotherProviderModal,
  [modalCodes.CONFIRM_UPDATE_GROUPED_APPOINTMENT_MODAL]: ConfirmUpdateGroupedAppointmentModal,
};

const propTypes = {
  modalShown: PropTypes.bool,
  typeId: PropTypes.string,
  onDismiss: PropTypes.func,
  rtl: PropTypes.bool,
};

const defaultProps = {
  modalShown: false,
  typeId: null,
  onDismiss: noop,
  rtl: false,
};

const ModalsPlace = ({ modalShown, typeId, ...rest }) => {
  return modalShown ? (
    <Modal
      Component={ (typeId && modalMap[typeId]) || DefaultModal }
      { ...rest }
    />
  ) : null;
};

ModalsPlace.propTypes = propTypes;
ModalsPlace.defaultProps = defaultProps;

export default ModalsPlace;
